import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, Loader2Icon, MailIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
const EMAILJS_SERVICE_ID = 'service_4y95tzd';
const EMAILJS_TEMPLATE_ID = 'template_9rwxvli';
const EMAILJS_PUBLIC_KEY = 'R97Q3Pi4glvxrRw2H';
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSfBODBHmUWDpM25YHczkaew5ovS2WRJtsaGNL6bD1Mo95FZxA/formResponse';
const GOOGLE_FORM_EMAIL_ENTRY_ID = 'entry.937191389';
const OTP_EXPIRY_MS = 10 * 60 * 1000;
const RESEND_COOLDOWN_SECONDS = 30;
export const DECK_UNLOCK_STORAGE_KEY = 'openfpga_ai_deck_unlocked';
type EmailGateProps = {
  isOpen: boolean;
  onClose: () => void;
  onUnlock: () => void;
};
type GateStep = 'email' | 'verify';
export function EmailGate({
  isOpen,
  onClose,
  onUnlock
}: EmailGateProps) {
  const [step, setStep] = useState<GateStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [enteredOtp, setEnteredOtp] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown(current => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);
  const handleClose = () => {
    if (isLoading) return;
    setError(null);
    onClose();
  };
  const sendOtp = async (targetEmail: string) => {
    const code = generateOtp();
    await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
      to_email: targetEmail,
      otp_code: code,
      from_name: 'OpenFPGA'
    }, EMAILJS_PUBLIC_KEY);
    setOtp(code);
    setOtpExpiry(Date.now() + OTP_EXPIRY_MS);
    setEnteredOtp('');
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };
  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    try {
      await sendOtp(email.trim().toLowerCase());
      setStep('verify');
    } catch (sendError) {
      console.error('Email verification code could not be sent.', sendError);
      setError('We could not send your code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleVerifySubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (enteredOtp.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    if (Date.now() > otpExpiry) {
      setError('This code has expired. Please request a new one.');
      setEnteredOtp('');
      return;
    }
    if (enteredOtp !== otp) {
      setError('That code is not correct. Please try again.');
      setEnteredOtp('');
      return;
    }
    logVerifiedEmail(email);
    localStorage.setItem(DECK_UNLOCK_STORAGE_KEY, 'true');
    onUnlock();
  };
  const handleResend = async () => {
    if (resendCooldown > 0 || isLoading) return;
    setError(null);
    setIsLoading(true);
    try {
      await sendOtp(email);
    } catch (sendError) {
      console.error('Email verification code could not be resent.', sendError);
      setError('We could not resend your code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const returnToEmail = () => {
    if (isLoading) return;
    setStep('email');
    setEnteredOtp('');
    setError(null);
  };
  return <AnimatePresence data-id="element-26" data-mp="1nvra532wmw5o">
      {isOpen && <motion.div aria-modal="true" role="dialog" aria-labelledby="email-gate-title" aria-describedby="email-gate-description" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-bg/90 px-5 py-8 backdrop-blur-sm" data-id="element-27" data-mp="1nuq091xmkfpj">
          <motion.div key={step} initial={{
        opacity: 0,
        y: 14,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative w-full max-w-md rounded-2xl border border-white/[0.1] bg-panel p-6 shadow-2xl shadow-black/50 sm:p-8" data-id="element-28" data-mp="6kav48znsadk">
            <button type="button" onClick={handleClose} disabled={isLoading} aria-label="Close access form" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-40" data-id="element-29" data-mp="3k23bvoufo2nc">
              <XIcon className="h-4 w-4" data-id="element-30" data-mp="2vfm5xsyfqeny" />
            </button>

            <div className="mb-7 flex h-11 w-11 items-center justify-center rounded-lg border border-accent/25 bg-accent/10 text-accent" data-id="element-31" data-mp="2qhcb5jgmhg2v">
              <span className="h-4 w-4 rounded-sm border-2 border-current" data-id="element-32" data-mp="35bgt91dlsxx2" />
            </div>

            {step === 'email' ? <>
                <div className="max-w-sm" data-id="element-33" data-mp="3edgcm0c3ce36">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent" data-id="element-34" data-mp="3ecf2pz6t9xn1">
                    OpenFPGA Deck Access
                  </p>
                  <h2 id="email-gate-title" className="mt-3 text-3xl font-extrabold tracking-tight text-white" data-id="element-35" data-mp="3o4ay32hij9nk">
                    Access Code Required
                  </h2>
                  <p id="email-gate-description" className="mt-3 text-sm leading-relaxed text-slate-400" data-id="element-36" data-mp="zbntf745060">
                    We will send a 6-digit access code to your email.
                  </p>
                </div>

                <form onSubmit={handleEmailSubmit} className="mt-7 space-y-4" data-id="element-37" data-mp="41ptv1u3svdv">
                  <label className="block" data-id="element-38" data-mp="8zrp0l4t0l5p">
                    <span className="sr-only" data-id="element-39" data-mp="90t1mu1gxfpj">Email address</span>
                    <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="name@company.com" autoComplete="email" autoFocus disabled={isLoading} className="w-full rounded-lg border border-white/[0.1] bg-bg px-4 py-3 text-sm text-white outline-none placeholder:text-slate-600 focus:border-accent focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50" data-id="element-40" data-mp="dykpncyzshog" />
                  </label>
                  {error && <p className="text-sm text-red-300" data-id="element-41" data-mp="2yitcvzxpe561">{error}</p>}
                  <button type="submit" disabled={isLoading} className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-bold text-bg transition-colors hover:bg-[#5ee0bc] disabled:cursor-not-allowed disabled:opacity-60" data-id="element-42" data-mp="33h3a0g2znfua">
                    {isLoading ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-43" data-mp="16utza2zd7o11" />
                        Sending code
                      </> : <>
                        Send access code
                        <ArrowRightIcon className="h-4 w-4" data-id="element-44" data-mp="1ziznx0tjhx65" />
                      </>}
                  </button>
                </form>
                <p className="mt-5 text-xs leading-relaxed text-slate-500" data-id="element-45" data-mp="1pktii98iy8wj">
                  Your verified email may be used to contact you about OpenFPGA.
                </p>
              </> : <>
                <div className="max-w-sm" data-id="element-46" data-mp="e3ac67zndptr">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent" data-id="element-47" data-mp="2e7e7hb1rnmhb">
                    <MailIcon className="h-4 w-4" data-id="element-48" data-mp="2j6qubvsn0tjw" />
                  </div>
                  <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-accent" data-id="element-49" data-mp="248yfl5ux096r">
                    Check your inbox
                  </p>
                  <h2 id="email-gate-title" className="mt-3 text-3xl font-extrabold tracking-tight text-white" data-id="element-50" data-mp="298t79bmil2sv">
                    Enter your code
                  </h2>
                  <p id="email-gate-description" className="mt-3 text-sm leading-relaxed text-slate-400" data-id="element-51" data-mp="31x8lvx1qhj3o">
                    We sent a 6-digit code to <span className="font-medium text-slate-200" data-id="element-52" data-mp="2n79npua6i7o7">{email}</span>.
                  </p>
                </div>

                <form onSubmit={handleVerifySubmit} className="mt-7 space-y-5" data-id="element-53" data-mp="2s4t00onbgadi">
                  <OtpInput value={enteredOtp} onChange={setEnteredOtp} disabled={isLoading} data-id="element-54" data-mp="3fux1oqgy201v" />
                  {error && <p className="text-center text-sm text-red-300" data-id="element-55" data-mp="3kohve6bi87v9">{error}</p>}
                  <button type="submit" disabled={isLoading || enteredOtp.length !== 6} className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-bold text-bg transition-colors hover:bg-[#5ee0bc] disabled:cursor-not-allowed disabled:opacity-50" data-id="element-56" data-mp="196wqt6vujplq">
                    {isLoading ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-57" data-mp="q8k8auj1p3dh" /> : 'Verify and open deck'}
                  </button>
                </form>

                <div className="mt-5 flex items-center justify-between gap-4 text-xs" data-id="element-58" data-mp="lcda5nww4w63">
                  <button type="button" onClick={returnToEmail} disabled={isLoading} className="text-slate-500 transition-colors hover:text-white disabled:opacity-40" data-id="element-59" data-mp="zbcyeughr91q">
                    Use a different email
                  </button>
                  <button type="button" onClick={handleResend} disabled={resendCooldown > 0 || isLoading} className="flex items-center gap-1.5 text-slate-400 transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-40" data-id="element-60" data-mp="bdayffxst108">
                    <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-61" data-mp="3tqil91kc9kvv" />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
type OtpInputProps = {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
};
function OtpInput({
  value,
  onChange,
  disabled
}: OtpInputProps) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const updateAtIndex = (index: number, rawValue: string) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    const digits = Array.from({
      length: 6
    }, (_, digitIndex) => value[digitIndex] ?? '');
    digits[index] = digit;
    onChange(digits.join(''));
    if (digit && index < 5) inputs.current[index + 1]?.focus();
  };
  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace') return;
    if (value[index]) {
      const digits = Array.from({
        length: 6
      }, (_, digitIndex) => value[digitIndex] ?? '');
      digits[index] = '';
      onChange(digits.join(''));
      return;
    }
    if (index > 0) {
      inputs.current[index - 1]?.focus();
      const digits = Array.from({
        length: 6
      }, (_, digitIndex) => value[digitIndex] ?? '');
      digits[index - 1] = '';
      onChange(digits.join(''));
    }
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedCode = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pastedCode);
    inputs.current[Math.min(pastedCode.length, 5)]?.focus();
  };
  return <div className="flex justify-between gap-2" aria-label="Six-digit access code" data-id="element-62" data-mp="nula5usx6y65">
      {Array.from({
      length: 6
    }, (_, index) => <input key={index} ref={element => {
      inputs.current[index] = element;
    }} type="text" inputMode="numeric" autoComplete={index === 0 ? 'one-time-code' : 'off'} maxLength={1} value={value[index] ?? ''} onChange={event => updateAtIndex(index, event.target.value)} onKeyDown={event => handleKeyDown(index, event)} onPaste={handlePaste} disabled={disabled} aria-label={`Access code digit ${index + 1}`} className={`h-12 min-w-0 flex-1 rounded-lg border bg-bg text-center font-mono text-lg font-semibold text-white outline-none transition-colors focus:border-accent focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50 ${value[index] ? 'border-accent/60' : 'border-white/[0.1]'}`} data-id="element-63" data-mp="iz5sssvpek4z" />)}
    </div>;
}
function generateOtp() {
  return String(Math.floor(100000 + Math.random() * 900000));
}
function isValidEmail(value: string) {
  return /^\S+@\S+\.\S+$/.test(value.trim());
}
function logVerifiedEmail(email: string) {
  fetch(GOOGLE_FORM_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: new URLSearchParams({
      [GOOGLE_FORM_EMAIL_ENTRY_ID]: email
    })
  }).catch((formError: unknown) => {
    console.error('Verified email could not be logged to the form.', formError);
  });
}